import { formatDatabaseSizeBytes } from '@app/pages/admin/orchestration-database-connections.util';

export interface StorageOccupancySource {
  id: string;
  label: string;
  sizeBytes: number;
  hint?: string;
  objectCount?: number;
  children?: StorageOccupancySource[];
}

export interface StorageOccupancyRow {
  id: string;
  label: string;
  sizeBytes: number;
  sizeLabel: string;
  sharePercent: number;
  color: string;
  hint: string;
  objectCount: number | null;
  children: StorageOccupancyRow[];
}

const OCCUPANCY_COLORS = [
  '#2563eb',
  '#16a34a',
  '#d97706',
  '#dc2626',
  '#7c3aed',
  '#0891b2',
  '#db2777',
  '#4f46e5',
  '#65a30d',
  '#ea580c',
];

export function buildStorageOccupancy(
  sources: StorageOccupancySource[],
  totalBytes: number,
): StorageOccupancyRow[] {
  return sources.map((source, index) => toOccupancyRow(source, totalBytes, index));
}

export function occupancyWithVolume(
  sources: StorageOccupancySource[],
  volume: { totalBytes?: number | null; availableBytes?: number | null },
): StorageOccupancyRow[] {
  const usedBytes = sources.reduce((sum, source) => sum + Math.max(0, source.sizeBytes), 0);
  const totalBytes = volume.totalBytes;
  const availableBytes = volume.availableBytes;
  if (totalBytes == null || availableBytes == null || !(totalBytes > 0) || availableBytes < 0) {
    return buildStorageOccupancy(sources, usedBytes);
  }
  const otherBytes = Math.max(0, totalBytes - availableBytes - usedBytes);
  const stacked: StorageOccupancySource[] = [...sources];
  if (otherBytes > 0) {
    stacked.push({ id: 'other-on-disk', label: 'Other on disk', sizeBytes: otherBytes });
  }
  stacked.push({ id: 'free', label: 'Free', sizeBytes: availableBytes });
  return buildStorageOccupancy(stacked, totalBytes);
}

export function firstExpandableOccupancyId(rows: StorageOccupancyRow[]): string {
  return rows.find(row => row.children.length > 0)?.id ?? '';
}

function toOccupancyRow(
  source: StorageOccupancySource,
  parentTotalBytes: number,
  colorIndex: number,
): StorageOccupancyRow {
  const sizeBytes = Number.isFinite(source.sizeBytes) ? Math.max(0, source.sizeBytes) : 0;
  const children = (source.children ?? []).map((child, index) =>
    toOccupancyRow(child, sizeBytes, colorIndex + index + 1));
  return {
    id: source.id,
    label: source.label,
    sizeBytes,
    sizeLabel: formatDatabaseSizeBytes(sizeBytes),
    sharePercent: occupancySharePercent(sizeBytes, parentTotalBytes),
    color: source.id === 'free'
      ? '#94a3b8'
      : source.id === 'other-on-disk'
        ? '#cbd5e1'
        : OCCUPANCY_COLORS[colorIndex % OCCUPANCY_COLORS.length],
    hint: source.hint ?? '',
    objectCount: typeof source.objectCount === 'number' ? source.objectCount : null,
    children,
  };
}

function occupancySharePercent(sizeBytes: number, totalBytes: number): number {
  if (!(totalBytes > 0) || !(sizeBytes > 0)) {
    return 0;
  }
  return Math.round((sizeBytes / totalBytes) * 1000) / 10;
}
